import React from 'react';
import { IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CANVAS_WIDTH, getCanvasHeight, type StudioPageData, type StudioSettings } from '../constants';
import StudioPage from './Canvas/StudioPage';

const THUMBNAIL_SCALE = 0.16;
// Target a consistent thumbnail height on mobile regardless of aspect ratio,
// so the bottom dock height stays predictable for both post and story formats.
// Keep the original native thumbnail size so every page remains easy to read
// and tap; the canvas workspace, not this dock, absorbs the remaining space.
const MOBILE_THUMBNAIL_HEIGHT = 84;

interface PageNavigatorProps {
  pages: StudioPageData[];
  pageIndex: number;
  settings: StudioSettings;
  onChangePage: (index: number) => void;
  variant?: 'desktop' | 'mobile';
}

const PageNavigator: React.FC<PageNavigatorProps> = ({
  pages,
  pageIndex,
  settings,
  onChangePage,
  variant = 'desktop'
}) => {
  if (pages.length < 2) {
    return null;
  }

  const isMobile = variant === 'mobile';
  const canvasHeight = getCanvasHeight(settings.format);
  const scale = isMobile ? MOBILE_THUMBNAIL_HEIGHT / canvasHeight : THUMBNAIL_SCALE;
  const thumbnailHeight = canvasHeight * scale;

  return (
    <div className={`studio-page-navigator${isMobile ? ' mobile' : ''}`}>
      {!isMobile && (
        <IconButton
          size="small"
          aria-label="Previous page"
          disabled={pageIndex === 0}
          onClick={() => onChangePage(pageIndex - 1)}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      <div className="studio-page-thumbnails">
        {pages.map((page, index) => (
          <button
            type="button"
            key={`${page.layoutType}-${index}`}
            className={`studio-page-thumbnail${index === pageIndex ? ' active' : ''}`}
            onClick={() => onChangePage(index)}
            aria-label={`Page ${index + 1}`}
          >
            <span
              className="studio-page-thumbnail-canvas"
              style={{
                width: CANVAS_WIDTH * scale,
                height: thumbnailHeight
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: CANVAS_WIDTH,
                  height: getCanvasHeight(settings.format),
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
              >
                <StudioPage page={page} settings={settings} pageNumber={index + 1} />
              </span>
            </span>
            <span className="studio-page-thumbnail-number">{index + 1}</span>
          </button>
        ))}
      </div>
      {!isMobile && (
        <IconButton
          size="small"
          aria-label="Next page"
          disabled={pageIndex === pages.length - 1}
          onClick={() => onChangePage(pageIndex + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </div>
  );
};

export default PageNavigator;
